/**
 * PrintableReport
 * 
 * Component for rendering a high-fidelity, print-optimized version of 
 * business reports, including summaries and customer breakdowns.
 */
import type { TFunction } from 'i18next';
import type { CustomerDebtDetail } from './CustomerDetailsModal';

// ── Types (mirrored from Reports.tsx) ────────────────────────────────

interface CustomerBreakdown {
  customer_id: number;
  customer_name: string;
  invoices_count: number;
  invoices_amount: number;
  receipts_count: number;
  receipts_amount: number;
  unpaid_amount: number;
  aged_debt_amount: number;
  total_amount: number;
}

interface ReportSummary {
  from_date: string;
  to_date: string;
  total_invoices_amount: number;
  total_receipts_amount: number;
  total_unpaid_amount: number;
  total_amount: number;
  total_documents: number;
}

export interface PrintableReportProps {
  summary: ReportSummary;
  customers: CustomerBreakdown[];
  customerDetails: Map<number, CustomerDebtDetail>;
  debtAgeThresholdDays: number;
  t: TFunction;
  locale: string;
  dir: 'ltr' | 'rtl';
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatNIS(amount: number): string {
  return `₪${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(value?: string | null, locale?: string): string {
  if (!value) return '—';
  const d = new Date(`${value.slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatMonthLabel(month: string, locale?: string): string {
  const d = new Date(`${month}-01T00:00:00`);
  return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short' });
}

const STATUS_LABELS: Record<string, string> = {
  paid: '✓',
  overdue: '⚠',
  due_soon: '⏰',
  current: '—',
};

// ── Component ────────────────────────────────────────────────────────

export default function PrintableReport({
  summary,
  customers,
  customerDetails,
  debtAgeThresholdDays,
  t,
  locale,
}: PrintableReportProps) {
  const now = new Date().toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="print-report">
      {/* ── Report Header ── */}
      <div className="report-header">
        <h1>{t('reports.printTitle')}</h1>
        <p className="meta">
          {t('reports.printPeriod', { from: formatDate(summary.from_date, locale), to: formatDate(summary.to_date, locale) })}
        </p>
        <p className="meta">{t('reports.printGeneratedAt', { date: now })}</p>
      </div>

      {/* ── Summary ── */}
      <div className="summary-section">
        <h2>{t('reports.printSummary')}</h2>
        <table className="summary-table">
          <tbody>
            <tr>
              <td className="label">{t('reports.totalRevenue')}</td>
              <td className="value">{formatNIS(summary.total_invoices_amount)}</td>
              <td className="label">{t('reports.receiptsCount')}</td>
              <td className="value">{formatNIS(summary.total_receipts_amount)}</td>
            </tr>
            <tr>
              <td className="label">{t('reports.unpaidAmount')}</td>
              <td className="value highlight-warn">{formatNIS(summary.total_unpaid_amount)}</td>
              <td className="label">{t('reports.totalDocuments')}</td>
              <td className="value">{summary.total_documents}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Customer Sections ── */}
      {customers.map((cust, idx) => {
        const detail = customerDetails.get(cust.customer_id);
        return (
          <div key={cust.customer_id} className="customer-section">
            {/* Customer header */}
            <div className="customer-header">
              <div className="customer-title">
                <h3>{cust.customer_name || `#${cust.customer_id}`}</h3>
                <span className="customer-index">
                  {t('reports.printCustomerOf', { index: idx + 1, total: customers.length })}
                </span>
              </div>
              {detail && (detail.phone || detail.email) && (
                <div className="customer-contact">
                  <span className="contact-label">{t('reports.printContact')}:</span>
                  {detail.phone && <span>{detail.phone}</span>}
                  {detail.email && <span>{detail.email}</span>}
                </div>
              )}
            </div>

            {/* Customer KPI row */}
            <table className="kpi-table">
              <thead>
                <tr>
                  <th>{t('reports.invoicesAmount')}</th>
                  <th>{t('reports.receiptsAmount')}</th>
                  <th>{t('reports.unpaidAmount')}</th>
                  <th>{t('reports.agedDebtAmount', { days: debtAgeThresholdDays })}</th>
                  <th>{t('reports.totalAmount')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{formatNIS(cust.invoices_amount)}</td>
                  <td>{formatNIS(cust.receipts_amount)}</td>
                  <td className="highlight-warn">{formatNIS(cust.unpaid_amount)}</td>
                  <td className="highlight-danger">{formatNIS(cust.aged_debt_amount)}</td>
                  <td className="bold">{formatNIS(cust.total_amount)}</td>
                </tr>
              </tbody>
            </table>

            {/* Recent Documents */}
            {detail?.recent_documents?.length ? (
              <div className="documents-section">
                <h4>{t('reports.recentDocuments')}</h4>
                <table className="documents-table">
                  <thead>
                    <tr>
                      <th className="text-start">{t('reports.documentTypes')}</th>
                      <th className="text-start">#</th>
                      <th className="text-start">{t('reports.dueDate')}</th>
                      <th className="text-end">{t('reports.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.recent_documents.map((doc) => (
                      <tr key={`${doc.document_type}-${doc.document_number}`}>
                        <td>{doc.document_type_name || `Type ${doc.document_type}`}</td>
                        <td>{doc.document_number}</td>
                        <td>{formatDate(doc.document_date, locale)}</td>
                        <td className="text-end">{formatNIS(doc.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !detail && null
            )}

            {/* Monthly debt */}
            {detail?.monthly_debt?.length ? (
              <div className="monthly-debt-section">
                <h4>{t('reports.monthlyDebt')}</h4>
                <table className="monthly-debt-table">
                  <thead>
                    <tr>
                      <th className="text-start">{t('reports.periodThisMonth')}</th>
                      <th className="text-end">{t('reports.invoicesAmount')}</th>
                      <th className="text-end">{t('reports.paidAmount')}</th>
                      <th className="text-end">{t('reports.unpaidAmount')}</th>
                      <th className="text-center">{t('reports.dueDate')}</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.monthly_debt.map((m) => (
                      <tr key={m.month}>
                        <td>{formatMonthLabel(m.month, locale)}</td>
                        <td className="text-end">{formatNIS(m.invoices_amount)}</td>
                        <td className="text-end">{formatNIS(m.paid_amount)}</td>
                        <td className="text-end highlight-warn">{formatNIS(m.unpaid_amount)}</td>
                        <td className="text-center">{formatDate(m.due_date, locale)}</td>
                        <td className="text-center">{STATUS_LABELS[m.status] || m.status} {t(`reports.status.${m.status}`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ── Print Styles (injected into print window) ────────────────────────

export const PRINT_STYLES = `
  * { margin: 0; padding: 0; box-sizing: border-box; }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 11px;
    line-height: 1.5;
    color: #1a1a1a;
    padding: 12mm;
  }

  .print-report { width: 100%; }

  .report-header {
    text-align: center;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 2px solid #333;
  }
  .report-header h1 { font-size: 20px; margin-bottom: 4px; }
  .report-header .meta { font-size: 12px; color: #555; }

  .summary-section { margin-bottom: 24px; }
  .summary-section h2 { font-size: 14px; margin-bottom: 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  .summary-table { width: 100%; border-collapse: collapse; }
  .summary-table td { padding: 4px 8px; }
  .summary-table .label { color: #555; font-weight: 500; width: 20%; }
  .summary-table .value { font-weight: 700; width: 30%; }

  .customer-section {
    page-break-inside: avoid;
    margin-bottom: 24px;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  .customer-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 4px;
  }
  .customer-title { display: flex; align-items: baseline; gap: 12px; }
  .customer-title h3 { font-size: 14px; }
  .customer-index { font-size: 10px; color: #888; }
  .customer-contact { font-size: 10px; color: #555; display: flex; gap: 12px; }
  .contact-label { font-weight: 600; }

  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th, td { padding: 3px 6px; border: 1px solid #e0e0e0; font-size: 10px; }
  th { background: #f5f5f5; font-weight: 600; text-transform: uppercase; font-size: 9px; letter-spacing: 0.3px; }
  .text-start { text-align: start; }
  .text-end { text-align: end; }
  .text-center { text-align: center; }

  .highlight-warn { color: #92400e; font-weight: 600; }
  .highlight-danger { color: #991b1b; font-weight: 600; }
  .bold { font-weight: 700; }

  .documents-section, .monthly-debt-section { margin-top: 8px; }
  .documents-section h4, .monthly-debt-section h4 {
    font-size: 11px;
    margin-bottom: 4px;
    color: #333;
    border-bottom: 1px solid #eee;
    padding-bottom: 2px;
  }

  @media print {
    body { padding: 8mm; }
    .customer-section { page-break-inside: avoid; break-inside: avoid; }
    @page { margin: 10mm; }
  }
`;
